import {
	Button,
	DsxFilePdf,
	MdOutlineFileDownload,
	SkeletonArea,
} from '@dsx/react';
import type {
	ProductAssetItem,
	ProductResponse,
} from '../../../services/productApi';
import styles from './CallToAction.module.css';

interface CallToActionProps {
	product?: ProductResponse | null;
	loading?: boolean;
	onRequestQuote: () => void;
	onFileDownload?: (asset: ProductAssetItem, fileType?: string) => void;
}

export function CallToAction({
	product,
	loading = false,
	onRequestQuote,
	onFileDownload,
}: CallToActionProps) {
	const specSheets =
		product?.assets?.['spec-sheet'] ?? product?.assets?.['Specification Sheet'];
	const specSheet = specSheets?.[0];

	const revitFiles = product?.assets?.['revit-file'];
	const revitFile = revitFiles?.[0];

	const manuals = product?.assets?.['manual'];
	const manual = manuals?.[0];

	const handleSpecSheetClick = () => {
		if (!specSheet?.contentUrl) {
			return;
		}

		if (onFileDownload) {
			onFileDownload(specSheet, 'spec_sheet');
		}

		window.open(specSheet.contentUrl, '_blank');
	};

	const handleRevitFileClick = () => {
		if (!revitFile?.contentUrl) {
			return;
		}

		if (onFileDownload) {
			onFileDownload(revitFile, 'revit');
		}

		const link = document.createElement('a');
		link.href = revitFile.contentUrl;
		link.download = revitFile.altText || 'revit-file';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleManualClick = () => {
		if (!manual?.contentUrl) {
			return;
		}

		if (onFileDownload) {
			onFileDownload(manual, 'manual');
		}

		window.open(manual.contentUrl, '_blank');
	};

	if (loading) {
		return (
			<div className={styles['callToAction']}>
				<SkeletonArea
					height="40px"
					width="135px"
					borderRadius="border-radius-default"
				/>
				<SkeletonArea
					height="40px"
					width="135px"
					borderRadius="border-radius-default"
				/>
				<SkeletonArea
					height="40px"
					width="135px"
					borderRadius="border-radius-default"
				/>
			</div>
		);
	}

	if (!product) return null;

	return (
		<div className={styles['callToAction']}>
			<Button
				variant="primary"
				onClick={onRequestQuote}
				className={styles['ctaButton']}
			>
				Request Quote
			</Button>

			{specSheet && (
				<Button
					variant="subtle"
					onClick={handleSpecSheetClick}
					className={styles['ctaButton']}
					icon={DsxFilePdf}
				>
					Spec Sheet
				</Button>
			)}

			{revitFile && (
				<Button
					variant="subtle"
					onClick={handleRevitFileClick}
					className={styles['ctaButton']}
					icon={MdOutlineFileDownload}
				>
					Revit File
				</Button>
			)}

			{manual && (
				<Button
					variant="subtle"
					onClick={handleManualClick}
					className={styles['ctaButton']}
					icon={DsxFilePdf}
				>
					Manual
				</Button>
			)}
		</div>
	);
}
